<template>
  <div class="grid-container">
    <form :class="{'opacity-50': isWorking}" @submit.prevent="submit">
      <section-header class="mt-3" variants="dividing" size="xl">
        <heading level="1" size="xl" v-html="bp('text.editProfile')"></heading>
      </section-header>
      <pdl-callout v-if="message" :kind="messageType">
        <template #content>
          <span v-html="message" />
        </template>
      </pdl-callout>
      <div class="max-w-sm mb-6">
        <form-group
          v-model="firstNameValue"
          v-validate="'required'"
          type="text"
          name="firstName"
          :label="bp('profile.firstName')"
          :data-vv-as="bp('profile.firstName')"
          :error="errors.first('firstName')"
          :show-error-messages="true"
          :maxlength="19"
          required
        />
        <form-group
          v-model="lastNameValue"
          v-validate="'required'"
          type="text"
          name="lastName"
          :label="bp('profile.lastName')"
          :data-vv-as="bp('profile.lastName')"
          :error="errors.first('lastName')"
          :show-error-messages="true"
          :maxlength="19"
          required
        />
        <section v-if="!b2b">
          <div class="form-group w-1/4 md:w-1/2">
            <div class="form-label" v-html="bp('height')" />
            <div class="flex flex-col md:flex-row gap-4">
              <form-group
                v-model="heightIntegerValue"
                v-validate="'bodyParams'"
                class="flex-auto mb-0"
                type="text"
                :maxlength="1"
                :show-label="false"
                :show-error-messages="true"
                :name="helpText.heightInteger"
                :label="helpText.heightInteger"
                :help-text="helpText.heightInteger"
                :data-vv-as="helpText.heightInteger"
                :error="errors.first(helpText.heightInteger)"
              />
              <form-group
                v-model="heightRemainderValue"
                v-validate="'bodyParams:heightRemainder'"
                class="flex-auto mb-0"
                type="text"
                :maxlength="2"
                :show-label="false"
                :show-error-messages="true"
                :name="helpText.heightRemainder"
                :label="helpText.heightRemainder"
                :help-text="helpText.heightRemainder"
                :data-vv-as="helpText.heightRemainder"
                :error="errors.first(helpText.heightRemainder)"
              />
            </div>
          </div>
          <form-group
            v-model="weightValue"
            v-validate="'bodyParams'"
            class="w-1/4 md:w-1/2"
            type="text"
            name="weight"
            :maxlength="3"
            :label="bp('weight')"
            :data-vv-as="bp('weight')"
            :show-error-messages="true"
            :help-text="helpText.weight"
            :error="errors.first('weight')"
          />
          <em class="text-sm text-gray-60 italic mb-3 block" v-html="bp('message')" />
        </section>
        <div class="buttons">
          <trek-button primary type="submit" :disabled="isWorking" @click="submit">
            <span v-html="bp('text.account.profile.saveUpdates')"></span>
          </trek-button>
          <trek-button secondary @click="cancel"><span v-html="bp('text.account.profile.cancel')"></span></trek-button>
        </div>
      </div>
    </form>
  </div>
</template>
<script>
import get from 'lodash/get';
import {mapGetters} from 'vuex';
import {Validator} from 'vee-validate';
import Heading from '@/components/Heading';
import {PdlCallout} from '@pedal/pdl-callout';
import FormGroup from '@/components/FormGroup';
import TrekButton from '@/components/TrekButton';
import SectionHeader from '@/components/SectionHeader';
import {UserAccountBase} from '@/components/containers/my-account/utils/user-account-base';

export default {
  name: 'EditProfileContainer',
  components: {Heading, PdlCallout, FormGroup, TrekButton, SectionHeader},
  extends: UserAccountBase,
  props: {
    firstName: {
      type: String,
      default: '',
    },
    lastName: {
      type: String,
      default: '',
    },
    heightInteger: {
      type: String,
      default: null,
    },
    heightRemainder: {
      type: String,
      default: null,
    },
    weight: {
      type: String,
      default: null,
    },
    distanceUnit: {
      type: String,
      default: '',
      required: true,
    },
  },
  data() {
    return {
      firstNameValue: '',
      lastNameValue: '',
      heightIntegerValue: '',
      heightRemainderValue: '',
      weightValue: '',
    };
  },
  computed: {
    ...mapGetters('backend', ['b2b']),
    helpText() {
      return {
        heightInteger: this.distanceUnit === 'MILES' ? this.bp('feet') : this.bp('meters'),
        heightRemainder: this.distanceUnit === 'MILES' ? this.bp('inches') : this.bp('centimeters'),
        weight: this.distanceUnit === 'MILES' ? this.bp('lbs') : this.bp('kg'),
      };
    },
  },
  watch: {
    heightIntegerValue(val) {
      this.heightIntegerValue = val.replace(/[^\d]/g, '');
    },
    heightRemainderValue(val) {
      this.heightRemainderValue = val.replace(/[^\d]/g, '');
    },
    weightValue(val) {
      this.weightValue = val.replace(/[^\d]/g, '');
    },
  },
  mounted() {
    this.firstNameValue = this.firstName;
    this.lastNameValue = this.lastName;
    this.heightIntegerValue = this.heightInteger;
    this.heightRemainderValue = this.heightRemainder;
    this.weightValue = this.weight;
  },
  created() {
    Validator.extend('bodyParams', {
      getMessage: () => this.bp('height.error'),
      validate: (value, args) => {
        // if field is "heightRemainder" and value is:
        // not a number and not whitespace(I'm allowing whitespaces but I'll trim them before submitting form data)
        if (args[0] === 'heightRemainder' && /\D/.test(value) && !/\s/.test(value)) {
          return false;
        }

        if (args[0] !== 'heightRemainder') {
          if (
            /\D/.test(value.trim()) ||
            ((/\s/.test(value) || parseInt(value) === 0) && (parseInt(value) === 0 || /\D/.test(value.trim())))
          ) {
            return false;
          }
        }

        return true;
      },
    });
  },
  methods: {
    /**
     * Handle email update.
     * @return {Promise<void>}
     */
    async processUpdate() {
      this.isWorking = true;
      try {
        const response = await this.api.updateProfile({
          firstName: this.firstNameValue,
          lastName: this.lastNameValue,
          heightInteger: this.heightIntegerValue.trim(),
          heightRemainder: this.heightRemainderValue.trim(),
          weight: this.weightValue.trim(),
          CSRFToken: this.options.CSRFToken,
        });

        const isError = get(response.data.meta, 'feedback.type') !== 'success';
        const message = get(response.data.meta, 'feedback.message.basePropertyValue');
        this[isError ? 'displayError' : 'displaySuccess'](message);
      } catch (e) {
        this.displayError(e.message);
      } finally {
        this.isWorking = false;
      }
    },
  },
};
</script>
